/**
 * DreadSovereignSkillEffect — Specter veil-path apotheosis: paralysis aura.
 *
 * Project an aura of dread for 8 seconds. All enemies within 5 tiles are
 * paralyzed — they cannot move, attack, escalate alert, or call backup.
 * The player is fully visible during this window (no concealment bonus).
 *
 * Activation scan: applies dread_paralysis to all enemies in radius at the
 * moment of activation. Continuous scan: each update frame, newly-entered
 * enemies also receive dread_paralysis for the remaining duration.
 *
 * The dread_paralysis status effect is managed by the StatusEffectSystem.
 * Its durationMs is set to the remaining window time at the moment of
 * application — enemies that enter late get a shorter paralysis window.
 *
 * Instant activation. Very long cooldown. Fizzle at low XP. Costs 2 slots.
 *
 * Implements SkillEffect directly. Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Dread Sovereign
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DreadSovereignDeps {
  readonly targeting: {
    shouldFizzle(): boolean;
    /** All enemy IDs currently within the given pixel radius of the player. */
    getEnemiesInRadius(radiusPx: number): string[];
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      targetEntityId: string;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DreadSovereignConfig {
  readonly cooldownMs: number;
  readonly durationMs: number;
  readonly radiusTiles: number;
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: DreadSovereignConfig = {
  cooldownMs: 5000,   // very long cooldown tier
  durationMs: 8000,   // 8-second paralysis window
  radiusTiles: 5,     // 5-tile aura radius
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DreadSovereignSkillEffect implements SkillEffect {
  readonly skillId = 'dread_sovereign';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: DreadSovereignDeps;
  private readonly config: DreadSovereignConfig;

  private _cooldownRemaining = 0;
  private _durationRemaining = 0;
  private _fizzled = false;
  private _activationCount = 0;

  // Tracks enemies that have already been paralyzed (to avoid duplicate applies)
  private _paralyzedEnemyIds: Set<string> = new Set();

  constructor(deps: DreadSovereignDeps, config: Partial<DreadSovereignConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  get paralyzedEnemyIds(): ReadonlySet<string> { return this._paralyzedEnemyIds; }
  get durationRemaining(): number { return this._durationRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && this._durationRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    this._durationRemaining = this.config.durationMs;
    this._paralyzedEnemyIds.clear();
    this._activationCount++;

    // Initial scan: apply dread_paralysis to all enemies in radius
    this._scanAndParalyze();

    return true;
  }

  deactivate(): void {
    // Instant skill — no manual toggle deactivation
  }

  isActive(): boolean {
    return this._durationRemaining > 0;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (this._durationRemaining > 0) {
      this._durationRemaining = Math.max(0, this._durationRemaining - delta);

      if (this._durationRemaining > 0) {
        // Continuous scan: paralyze enemies that entered the radius this frame
        // (still active — paralysis duration = remaining window time)
        this._scanAndParalyze();
      } else {
        // Window expired — start cooldown now (not at activation)
        this._cooldownRemaining = this.config.cooldownMs;
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._durationRemaining = 0;
    this._paralyzedEnemyIds.clear();
  }

  // --- Private ---

  private _scanAndParalyze(): void {
    const radiusPx = this.config.radiusTiles * this.config.tileSizePx;
    const enemies = this.deps.targeting.getEnemiesInRadius(radiusPx);

    for (const enemyId of enemies) {
      if (!this._paralyzedEnemyIds.has(enemyId)) {
        this.deps.statusEffects.apply('dread_paralysis', {
          sourceSkillId: 'dread_sovereign',
          durationMs: this._durationRemaining,
          targetEntityId: enemyId,
        });
        this._paralyzedEnemyIds.add(enemyId);
      }
    }
  }
}
